import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  GlobalStyles,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { object, string } from 'yup';
import {
  createRotation,
  deleteRotation,
  deleteRotationWithGroupCleanup,
  updateRotation,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { useUserContext } from '../../lib/users-context';
import { Group, Rotation } from '../../lib/types';
import {
  FIRESTORE_BATCH_LIMIT,
  ROTATION_NAME_MAX_LENGTH,
} from '../../lib/constants';
import { countTeachers, sortGroups } from '../../lib/groups';
import { flagDetail, flagOn } from '../../lib/attendee-flags';
import RotationCard, {
  type FoodAllergyEntry,
  type GroupCount,
} from './rotation-card';

export const PRINT_TARGET_ID = 'rotations-print-target';

const MAX_REASSIGN_GROUPS = FIRESTORE_BATCH_LIMIT - 1;

// Mirrors the Roster's print stylesheet: hide all screen chrome, force black-
// on-white inside the print target, and page-break after every rotation card
// except the last (avoids a trailing blank page).
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '@page': { size: 'letter portrait', margin: '0.5in' },
        '.MuiAppBar-root': { display: 'none !important' },
        'footer, [role="contentinfo"]': { display: 'none !important' },
        '.MuiSnackbar-root, .MuiBackdrop-root, .MuiDialog-root': {
          display: 'none !important',
        },
        '.roster-print-page-header, .roster-print-subtabs': {
          display: 'none !important',
        },
        body: { backgroundColor: 'white !important' },
        [`#${PRINT_TARGET_ID}, #${PRINT_TARGET_ID} *`]: {
          color: 'black !important',
        },
        [`#${PRINT_TARGET_ID} .MuiPaper-root, #${PRINT_TARGET_ID} .MuiCard-root`]:
          {
            backgroundColor: 'white !important',
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
          },
        [`#${PRINT_TARGET_ID} .MuiTableCell-root`]: {
          borderColor: 'black !important',
        },
        [`#${PRINT_TARGET_ID} > .rotation-page`]: {
          breakAfter: 'page',
          pageBreakAfter: 'always',
          marginBottom: 0,
        },
        [`#${PRINT_TARGET_ID} > .rotation-page:last-child`]: {
          breakAfter: 'auto',
          pageBreakAfter: 'auto',
        },
      },
    }}
  />
);

type RotationFormValues = { name: string };

const buildRotationSchema = (
  existingRotations: Rotation[],
  excludeRef?: string
) =>
  object().shape({
    name: string()
      .trim()
      .required('Name is required')
      .max(
        ROTATION_NAME_MAX_LENGTH,
        `Name must be ${ROTATION_NAME_MAX_LENGTH} characters or less`
      )
      .test(
        'unique',
        'A rotation with this name already exists',
        function (v) {
          if (!v) return true;
          const candidate = v.trim().toLowerCase();
          const conflict = existingRotations.find(
            (r) =>
              r.ref !== excludeRef && r.name.toLowerCase() === candidate
          );
          return !conflict;
        }
      ),
  });

const RotationFormDialog = ({
  open,
  rotation,
  rotations,
  activeEventRef,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  rotation: Rotation | null;
  rotations: Rotation[];
  activeEventRef: string | undefined;
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const isEdit = rotation !== null;
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<RotationFormValues>(
    () => ({ name: rotation?.name || '' }),
    [rotation]
  );

  const validationSchema = useMemo(
    () => buildRotationSchema(rotations, rotation?.ref),
    [rotations, rotation?.ref]
  );

  const handleSubmit = async (
    values: RotationFormValues,
    { setSubmitting }: FormikHelpers<RotationFormValues>
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const payload = { name: values.name.trim() };
      if (rotation) {
        // Mid-session safety: refuse to apply edits if the admin flipped the
        // active event while this dialog was open — the rotation doc would
        // no longer belong to the event the user is viewing.
        if (!activeEventRef || rotation.eventRef !== activeEventRef) {
          onError('Active event changed — close and reopen this rotation');
          setSubmitting(false);
          setIsWorking(false);
          return;
        }
        await updateRotation(actor, rotation, payload);
      } else {
        if (!activeEventRef) {
          onError('No active event — set one in Admin first');
          setSubmitting(false);
          setIsWorking(false);
          return;
        }
        await createRotation(actor, {
          ...payload,
          eventRef: activeEventRef,
        });
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving rotation', error);
      onError(isEdit ? 'Failed to update rotation' : 'Failed to add rotation');
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit rotation' : 'Add rotation'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                name='name'
                label='Rotation name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={
                  (touched.name && errors.name) ||
                  `${values.name.length}/${ROTATION_NAME_MAX_LENGTH}`
                }
                inputProps={{ maxLength: ROTATION_NAME_MAX_LENGTH }}
                disabled={isSubmitting}
                fullWidth
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add rotation'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const DeleteRotationDialog = ({
  rotation,
  affectedCount,
  onClose,
  onDeleted,
  onError,
}: {
  rotation: Rotation | null;
  affectedCount: number;
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) => {
  const { groups } = useFirebaseContext();
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  const tooMany = affectedCount > MAX_REASSIGN_GROUPS;

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!rotation) return;
    if (tooMany) return;

    const liveAffectedRefs = groups
      .filter((g) => g.rotationRef === rotation.ref)
      .map((g) => g.ref);

    if (liveAffectedRefs.length > MAX_REASSIGN_GROUPS) {
      onError(
        `Rotation now has ${liveAffectedRefs.length} groups, exceeding the ${MAX_REASSIGN_GROUPS} limit for atomic reassignment.`
      );
      return;
    }

    setIsWorking(true);
    try {
      if (liveAffectedRefs.length > 0) {
        await deleteRotationWithGroupCleanup(actor, rotation, liveAffectedRefs);
      } else {
        await deleteRotation(actor, rotation);
      }
      setIsWorking(false);
      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting rotation', error);
      onError('Failed to delete rotation');
      setIsWorking(false);
    }
  };

  return (
    <Dialog
      open={Boolean(rotation)}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Delete rotation</DialogTitle>
      <DialogContent>
        {rotation && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Delete rotation <strong>{rotation.name}</strong>?
            </DialogContentText>
            {tooMany && (
              <Alert severity='error' sx={{ mb: 2 }}>
                This rotation has {affectedCount} groups, which exceeds the
                limit of {MAX_REASSIGN_GROUPS} for a single atomic
                unassignment.
              </Alert>
            )}
            {affectedCount > 0 && !tooMany && (
              <DialogContentText>
                {affectedCount} group{affectedCount === 1 ? '' : 's'} will
                become unassigned.
              </DialogContentText>
            )}
            {affectedCount === 0 && (
              <DialogContentText>
                No groups are assigned to this rotation.
              </DialogContentText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={isWorking || tooMany}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RotationsView = ({
  adding,
  onCloseAdd,
  kitchenMode = false,
}: {
  adding: boolean;
  onCloseAdd: () => void;
  // Kitchen-PDF mode: surfaces a per-group food-allergy list beneath
  // each group row, switches each rotation card to the head-count
  // total (checked-in kids + teachers), and tags every FA entry with
  // its check-in status. Driven by the dedicated "Kitchen PDF" button
  // in `groups-page.tsx`; the flag is true only for the brief window
  // between click and `afterprint` reset, so the on-screen view is
  // essentially never visible to the admin in this mode.
  kitchenMode?: boolean;
}) => {
  const { rotations, groups, attendees, activeCheckInDate, activeEvent } =
    useFirebaseContext();
  const { isAdmin } = useUserContext();
  const toast = useToast();
  const [editing, setEditing] = useState<Rotation | null>(null);
  const [deleting, setDeleting] = useState<Rotation | null>(null);

  const sortedRotations = useMemo(
    () =>
      [...rotations].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [rotations]
  );

  // Per-group counts: for the active check-in date, an attendee counts as
  // checked in iff their ref is in checkedInRefs and NOT in checkedOutRefs.
  // No active date => all counts are 0 / total.
  const countsByGroupRef = useMemo(() => {
    const checkedInSet = new Set(activeCheckInDate?.checkedInRefs || []);
    const checkedOutSet = new Set(activeCheckInDate?.checkedOutRefs || []);
    const map = new Map<string, GroupCount>();
    attendees.forEach((a) => {
      if (!a.groupRef) return;
      const c = map.get(a.groupRef) || { checkedIn: 0, total: 0 };
      c.total += 1;
      if (checkedInSet.has(a.ref) && !checkedOutSet.has(a.ref)) {
        c.checkedIn += 1;
      }
      map.set(a.groupRef, c);
    });
    return map;
  }, [attendees, activeCheckInDate]);

  // Per-group food-allergy roster. Surfaced as a sub-row beneath each
  // group on the Rotations card so the printed PDF works as a kitchen
  // handout (admin asked for "all food allergy records per group" on
  // 2026-06-08). Sorted by last/first name to match the Roster's order.
  // Legacy `food_allergies: 'Yes'` rows (pre free-text migration) are
  // still listed — flagOn() returns true, flagDetail() returns '' and
  // we surface those as "(no details on file)" so the kitchen at
  // least knows the kid has an asserted allergy. `isCheckedIn` is
  // computed unconditionally and is consumed only by the Kitchen PDF
  // (RotationCard ignores it in standard mode).
  const faByGroupRef = useMemo(() => {
    const checkedInSet = new Set(activeCheckInDate?.checkedInRefs || []);
    const checkedOutSet = new Set(activeCheckInDate?.checkedOutRefs || []);
    const map = new Map<string, FoodAllergyEntry[]>();
    attendees.forEach((a) => {
      if (!a.groupRef) return;
      if (!flagOn(a.food_allergies)) return;
      const entry: FoodAllergyEntry = {
        firstName: a.first_name,
        lastName: a.last_name,
        detail: flagDetail(a.food_allergies),
        isCheckedIn:
          checkedInSet.has(a.ref) && !checkedOutSet.has(a.ref),
      };
      const list = map.get(a.groupRef);
      if (list) list.push(entry);
      else map.set(a.groupRef, [entry]);
    });
    map.forEach((list) => {
      list.sort((x, y) => {
        const lastDiff = x.lastName.localeCompare(y.lastName, undefined, {
          sensitivity: 'base',
        });
        if (lastDiff !== 0) return lastDiff;
        return x.firstName.localeCompare(y.firstName, undefined, {
          sensitivity: 'base',
        });
      });
    });
    return map;
  }, [attendees, activeCheckInDate]);

  // Bucket groups by rotation. Unknown / missing rotationRef => unassigned.
  const { groupsByRotation, unassignedGroups } = useMemo(() => {
    const validRotationRefs = new Set(rotations.map((r) => r.ref));
    const byRotation = new Map<string, Group[]>();
    const unassigned: Group[] = [];
    groups.forEach((g) => {
      if (g.rotationRef && validRotationRefs.has(g.rotationRef)) {
        const list = byRotation.get(g.rotationRef);
        if (list) list.push(g);
        else byRotation.set(g.rotationRef, [g]);
      } else {
        unassigned.push(g);
      }
    });
    return {
      groupsByRotation: byRotation,
      unassignedGroups: sortGroups(unassigned),
    };
  }, [rotations, groups]);

  const isEmpty =
    sortedRotations.length === 0 && unassignedGroups.length === 0;

  // FA list is kitchen-mode-only. The standard rotations PDF
  // intentionally omits it (admins use the Kitchen PDF for that).
  const faMap = kitchenMode ? faByGroupRef : undefined;

  // Event-wide head-count totals for the Kitchen PDF's lead summary
  // page. Covers every group (rotation-assigned + unassigned) so the
  // total matches the per-page sums. Teachers come from each group's
  // free-text `teachers` field; kids are checked-in counts.
  const eventTotals = useMemo(() => {
    let kids = 0;
    let teachers = 0;
    groups.forEach((g) => {
      const c = countsByGroupRef.get(g.ref);
      if (c) kids += c.checkedIn;
      teachers += countTeachers(g.teachers);
    });
    return { kids, teachers, total: kids + teachers };
  }, [groups, countsByGroupRef]);

  return (
    <>
      {printStyles}
      <Box id={PRINT_TARGET_ID}>
        {isEmpty && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color='text.secondary'>
              No rotations yet. Click <strong>+ Add rotation</strong> in the
              page header to create one.
            </Typography>
          </Box>
        )}
        {kitchenMode && !isEmpty && (() => {
          // Volunteer head-count is event-level metadata populated
          // externally (see AppEvent.volunteer_count_today). When the
          // field is a number it gets a line above the divider and is
          // rolled into the grand total; absent => the line is omitted
          // and the grand total reverts to kids + teachers.
          const volunteers =
            typeof activeEvent?.volunteer_count_today === 'number'
              ? activeEvent.volunteer_count_today
              : null;
          const grandTotal = eventTotals.total + (volunteers ?? 0);
          const grandTotalLabel =
            volunteers === null
              ? 'Total (kids + teachers):'
              : 'Total (kids + teachers + volunteers):';
          return (
            <Box className='rotation-page'>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    All Rotations
                  </Typography>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography variant='body1'>
                        Total kids (checked-in):
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {eventTotals.kids}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography variant='body1'>Total teachers:</Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {eventTotals.teachers}
                      </Typography>
                    </Box>
                    {volunteers !== null && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Typography variant='body1'>
                          Checked in volunteers:
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 600 }}>
                          {volunteers}
                        </Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {grandTotalLabel}
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {grandTotal}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          );
        })()}
        {sortedRotations.map((r) => {
          const rotationGroups = sortGroups(groupsByRotation.get(r.ref) || []);
          return (
            <Box key={r.ref} className='rotation-page'>
              <RotationCard
                title={r.name}
                groups={rotationGroups}
                countsByGroupRef={countsByGroupRef}
                faByGroupRef={faMap}
                kitchenMode={kitchenMode}
                onEdit={() => setEditing(r)}
                onDelete={isAdmin ? () => setDeleting(r) : undefined}
              />
            </Box>
          );
        })}
        {unassignedGroups.length > 0 && (
          <Box className='rotation-page'>
            <RotationCard
              title={`Unassigned (${unassignedGroups.length})`}
              summaryLabel='Unassigned'
              groups={unassignedGroups}
              countsByGroupRef={countsByGroupRef}
              faByGroupRef={faMap}
              kitchenMode={kitchenMode}
            />
          </Box>
        )}
      </Box>

      <RotationFormDialog
        open={adding || editing !== null}
        rotation={editing}
        rotations={rotations}
        activeEventRef={activeEvent?.ref}
        onClose={() => {
          setEditing(null);
          if (adding) onCloseAdd();
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'Rotation added' : 'Rotation saved')
        }
        onError={(message) => toast.error(message)}
      />

      <DeleteRotationDialog
        rotation={deleting}
        affectedCount={
          deleting ? (groupsByRotation.get(deleting.ref) || []).length : 0
        }
        onClose={() => setDeleting(null)}
        onDeleted={() => toast.success('Rotation deleted')}
        onError={(message) => toast.error(message)}
      />
    </>
  );
};

export default RotationsView;
